/* 
 * ProblemLinkingMediator.js
 * Using the problem model to create draggable solution and argument buttons that need to be matched
 * 
 * @author Olivier van der Pool for Ranj
 */

import DraggableComponent from "/lib/html5-software-library/src/ranj/components/draganddrop/DraggableComponent";
import DraggableComponentEvent from "/lib/html5-software-library/src/ranj/components/draganddrop/DraggableComponentEvent";
import { isNull, isUndefined } from "/lib/html5-software-library/src/ranj/util/TypeUtil";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ProblemLinkingCommandEvent from "../controller/ProblemLinkingCommandEvent";

export default class ProblemLinkingMediator extends AbstractMediator {
        constructor(target, dispatcher, soundManager, assetLibrary, scenarioLibrary) {
            super(target, dispatcher);
            this._soundManager = soundManager;
            this._assetLibrary = assetLibrary;
            this._scenarioLibrary = scenarioLibrary;
            
            this._problemModel = scenarioLibrary.getCurrentScenario().problemModel;
            this._problem = this._problemModel.getProblemByID("australia_kangaroos");
            this._solutions = {};
            this._arguments = {};
            
            this._selectedSolution = null;
            this._selectedArguments = [null, null, null, null]; //a null value for each argument slot
            
            this._argumentTargets = [];
            this._solutionTargets = []; //the targets of a draggable component must be in an array, even if there is only one target
            this._solutionTargets.push(this._target["solutionTarget"]);
            
            for(var i = 1; i <= 4; i++) {
                this._argumentTargets.push(this._target[`factTarget${i}`]);
            }
            
            for(i = 1; i <= 2; i++) {
               this._target[`solution${i}`].visible = false;
            }
            for(i = 1; i <= 4; i++) {
               this._target[`argument${i}`].visible = false;
            }
            
            this._submitButton = this.createButton(this._target.submitButton, this._handleSubmitClick.bind(this));
            this._submitButton.setVisible(false);
        }

        initialize() {
            super.initialize();
            let i;
            let skin;
            let dragButton;
            this._target.problemDescriptionField.text = this._problem.getDescription();
            this._onSolutionDropFunction = this._onSolutionDrop.bind(this);
            this._onArgumentDropFunction = this._onArgumentDrop.bind(this);

            //add a drag button to the stage for each solution
            const solutions = this._problem.getSolutions();
            for(i = 0; i < solutions.length; i++) {
               skin = this._target[`solution${i+1}`];
               skin.visible = true;
               skin.descriptionField.text = solutions[i].getDescription();
               dragButton = new DraggableComponent(skin, this._solutionTargets, .8, "#0FF", 5, true, 0, 0);
               this._target.addChild(dragButton);
               this._solutions[solutions[i].getID()] = dragButton;
               dragButton.eventDispatcher.addEventListener(DraggableComponentEvent.DRAG_END, this._onSolutionDropFunction);
            }

            //add argument buttons to the stage
            const args = this._problemModel.getPossibleArguments();
            for(i = 0; i < args.length; i++) {
                skin = this._target[`argument${i+1}`];
                skin.visible = true;
                skin.descriptionField.text = args[i].getDescription();
                dragButton = new DraggableComponent(skin, this._argumentTargets, .8, "#000", 5, true, 0, 0);
                this._target.addChild(dragButton);
                this._arguments[args[i].getID()] = dragButton;
               dragButton.eventDispatcher.addEventListener(DraggableComponentEvent.DRAG_END, this._onArgumentDropFunction);
            }
        }

        _onSolutionDrop({data}) {
            const button = data.source;
            const target = data.destination;
            let solution;
            
            //get the solutionID that belongs to this button
            for(solution in this._solutions) {
                if(this._solutions[solution] === button) break;
            }
            
        if(!isNull(this._selectedSolution) && this._selectedSolution !== solution) {
                //there's currently another button in the selection slot
                const otherButton = this._solutions[this._selectedSolution];
                otherButton.moveToSnappingPoint();
            } else if (isUndefined(target) && (this._selectedSolution === solution || this._selectedSolution === null)) {
                //the currently selected solution is dropped out of the selection box
                solution = null;
            }
            
            this._selectedSolution = solution;
            this._submitButton.setVisible(!isNull(solution)); 
        }

        _onArgumentDrop({data}) {
            const button = data.source;
            const target = data.destination;
            const index = this._argumentTargets.indexOf(target); //the index of the target slot
            let argument; 
            
            //get the argumentID that belongs to this button
            for(argument in this._arguments) {
                if(this._arguments[argument] === button) break;
            }
            
            if(!isNull(this._selectedArguments[index]) && this._selectedArguments[index] !== argument) {
                //there's currently another button in the selection slot
                const otherButton = this._arguments[this._selectedArguments[index]];
                otherButton.moveToSnappingPoint();
            } else if (isUndefined(target) && this._selectedArguments[index] === argument) {
                //the currently selected solution is dropped out of the selection box
                argument = null;
            }
            
            this._selectedArguments[index] = argument;
        }

        _handleSubmitClick() {
            const event = new ProblemLinkingCommandEvent(ProblemLinkingCommandEvent.PROBLEM_LINKING_SUBMITTED);        
            this.dispatch(event.type, {correctModel: this._problemModel, selectedSolution: this._selectedSolution, problem: this._problem, selectedArguments: this._selectedArguments});
        }

        destroy() {
            super.destroy();
        }
    }